import { Button } from '@/components/base/button'
import { ButtonSortAlpha } from '@/components/ButtonSortAlpha'
import { Icon } from '@/components/common/icons/Icon'
import { Dialpad } from '@/components/Dialpad'
import { DirectoryList } from '@/components/DirectoryList'
import { SearchButton } from '@/components/SearchButton'
import {
  BREAKPOINT_KEYS,
  DEFAULT_CONTEXT,
  DIALER_CALL_CONTEXT,
  SORT_ORDER,
} from '@/helpers/constants'
import type { CountryName } from '@/helpers/country'
import type { Resource } from '@/helpers/types'
import { cn, sortBy, startsWithPSTN, startsWithSIP } from '@/helpers/utils'
import { validatePhoneNumber, validateSipAddress } from '@/helpers/validation'
import { useBreakPoint } from '@/hooks/useBreakPoint'
import { useSimpleMultiKeySearch } from '@/hooks/useSimpleMultiKeySearch'
import { useSortOrder } from '@/hooks/useSortOrder'
import { useSubscribeConversations } from '@/hooks/useSubscribeConversations'
import { useResourcesStore, useResourcesStoreActions } from '@/stores/resources'
import { useUserStore } from '@/stores/user'
import { useMemo, useRef, useState } from 'react'

const searchFriendlyName = (resource: Resource) => {
  // Normalize the display name to be used in the directory
  return resource.displayName

    .replace(/[-()]/g, '') // remove hyphens, parentheses
    .replace(/\s+/g, '') // remove multiple spaces
    .trim() // remove leading and trailing spaces
    .toLocaleLowerCase()
}

const formatAudioRoomPath = (searchTerm: string) => {
  const isKnownPrefix = (searchTerm: string) =>
    startsWithPSTN(searchTerm) || startsWithSIP(searchTerm)

  // TODO: should SIP/PSTN be handled with the `/private` context always?
  const searchTermToDestinationUri = (searchTerm: string) => {
    if (isKnownPrefix(searchTerm)) {
      return `/${DEFAULT_CONTEXT}/${searchTerm}`
    } else {
      return `/${DIALER_CALL_CONTEXT}/${searchTerm}`
    }
  }

  const toURI = searchTermToDestinationUri(searchTerm)
  return toURI
}

const deriveAudioChannel = (
  countryName: CountryName,
  selectResource: Resource | null,
  value: string,
) => {
  if (selectResource?.channels.audio) {
    return selectResource?.channels.audio
  } else if (value) {
    // check if the dialpad value is a valid phone number
    const parsedNumber = validatePhoneNumber(value, countryName as CountryName)
    if (parsedNumber.success) {
      return `/${DIALER_CALL_CONTEXT}/${parsedNumber.data}?channel=audio`
    }

    // try SIP
    const parsedSip = validateSipAddress(value)
    if (parsedSip.success) {
      return `/${DIALER_CALL_CONTEXT}/${parsedSip.data}?channel=audio`
    }

    // FIXME: is this still required if sip and phone numbers are handled above?
    return formatAudioRoomPath(`${value}?channel=audio`)
  } else {
    // default to empty string
    return ''
  }
}

export const CallsView = () => {
  const country = useUserStore(state => state.country)
  const [dialpadValue, setDialpadValue] = useState('')
  const [showDirectoryPanel, setShowDirectoryPanel] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)
  const selectedResourceRef = useRef<Resource | null>(null)

  const resourcesByIdMap = useResourcesStore(state => state.resourcesByIdMap)

  const { getResourcesFromMap, getResourceByName } = useResourcesStoreActions()

  const { sortOrder, toggleSortOrder } = useSortOrder()

  const isBreakPointLg = useBreakPoint() === BREAKPOINT_KEYS.LG

  // TODO: remove when subscription is implemented at top level of the app
  useSubscribeConversations()

  const sortedResources = useMemo(() => {
    const resources = getResourcesFromMap(resourcesByIdMap)
    const normalized = resources.map(resource => ({
      ...resource,
      searchFriendlyName: searchFriendlyName(resource),
    }))
    return sortBy(normalized, sortOrder, 'displayName')
  }, [resourcesByIdMap, getResourcesFromMap, sortOrder])

  const { searchResults, handleSearch, handleSearchReset, searchTerm } =
    // TODO: replace with a better backend based search implementation
    useSimpleMultiKeySearch({
      items: sortedResources,
      keyMinLength: 1,
      keys: ['displayName', 'name', 'searchFriendlyName'],
    })

  const updateSelectedResource = (resourceName: Resource['name']) => {
    const resource = getResourceByName(resourceName)
    if (resource) {
      selectedResourceRef.current = resource
    } else {
      selectedResourceRef.current = null
    }
  }

  const setDialpadValueAndResource = (value: string) => {
    setDialpadValue(value)
    updateSelectedResource(value)
  }

  const handleShowPasteOption = (event: React.MouseEvent) => {
    // NOTE: to show the paste option in the native browser context menu we
    // have a HACK to temporarily set the contentEditable attribute to true
    const parentElement = event.currentTarget as HTMLElement
    parentElement.setAttribute('contentEditable', 'true')
    parentElement.focus()
    setTimeout(() => {
      parentElement?.removeAttribute('contentEditable')
    }, 0)
  }

  const handlePaste = (event: React.ClipboardEvent) => {
    // prevent paste on the target element
    event.preventDefault()

    // paste to the dialpad input
    const clipboardData = event.clipboardData
    if (clipboardData) {
      const pastedData = clipboardData.getData('text')
      // set the value of the input to the pasted data
      setDialpadValueAndResource(pastedData)
      inputRef.current?.focus()
    }
  }

  const handleClickResource = (resource: Resource) => {
    setDialpadValueAndResource(resource.name)
    inputRef.current?.focus()

    if (!isBreakPointLg) {
      // close the directory panel if open
      setShowDirectoryPanel(false)
    }
  }

  const handleDialpadChange = (value: string) => {
    // search the directory with the dialpad input value
    handleSearch({
      search: value,
    })
    setDialpadValueAndResource(value)
  }

  const handleOpenDirectory = () => {
    setShowDirectoryPanel(true)
  }

  const handleCloseDirectory = () => {
    setShowDirectoryPanel(false)
  }

  const audioChannel = deriveAudioChannel(
    country as CountryName,
    selectedResourceRef.current,
    dialpadValue,
  )

  return (
    <div className="flex h-full flex-col">
      <div className="relative flex h-full">
        <div
          className={cn(
            'flex w-full flex-col',
            isBreakPointLg && 'max-w-80',
            !isBreakPointLg &&
              'absolute left-0 top-0 z-10 h-full -translate-x-full bg-background transition-transform duration-300 ease-in-out',
            showDirectoryPanel && 'translate-x-0',
          )}
        >
          <section className="flex flex-col gap-y-1 pb-1 pl-6 pr-5 pt-2">
            <div className="flex items-center justify-between">
              <h2 className="text-4xl font-semibold leading-snug">Calls</h2>
              <Button
                className="bg-background p-0 text-foreground"
                variant="icon"
                // TODO: implement the back/close "panel logic"
                onClick={handleCloseDirectory}
              >
                <Icon tag="close" size="xl" />
              </Button>
            </div>
          </section>
          <div className="flex flex-col gap-y-1 pb-3.5 pl-6 pr-5 pt-2">
            <div className="relative flex items-center justify-between">
              <h3 className="absolute left-0 top-1/2 -translate-y-1/2 text-[22px] font-normal leading-7">
                Directory
              </h3>
              <ul
                aria-label="Directory search, sort options"
                className="flex grow items-center justify-end"
              >
                <li className={cn('grow', !isBreakPointLg && 'max-w-96')}>
                  <div className="relative flex w-full grow justify-end">
                    <SearchButton
                      // TODO: need UI/UX feedback on the expanding search button
                      buttonLabel="Open search for directory"
                      formDescription="Search for a resource"
                      handleSearch={handleSearch}
                      handleSearchReset={handleSearchReset}
                      placeholder="Search Directory"
                    />
                  </div>
                </li>
                <li>
                  <ButtonSortAlpha
                    disabled={false} // TODO: disable when fetching resources
                    isAsc={sortOrder === SORT_ORDER.ASC}
                    onClick={toggleSortOrder}
                  />
                </li>
              </ul>
            </div>
          </div>
          <DirectoryList
            onClickResource={handleClickResource}
            resources={searchResults}
            searchTerm={searchTerm}
          />
        </div>
        <div
          className="flex h-full w-full flex-col items-center justify-center bg-primary"
          onContextMenu={handleShowPasteOption}
          onPaste={handlePaste}
        >
          <Dialpad
            audioChannel={audioChannel}
            dialpadValue={dialpadValue}
            onClickDirectory={handleOpenDirectory}
            onDialpadChange={handleDialpadChange}
            ref={inputRef}
            redirect="/calls"
            showDirectoryBtn={isBreakPointLg}
            videoChannel={selectedResourceRef.current?.channels.video || ''}
          />
        </div>
      </div>
    </div>
  )
}
